import React, { useEffect, useRef, useState } from "react";
import { previewTemplate } from "./remindersApi";

/**
 * Removes external `@import` font rules from preview HTML.
 *
 * Email templates pull a web font via `@import url(https://…)`. Inside the
 * sandboxed preview iframe that request blocks first paint until it resolves,
 * and stays blank when the font host is slow or blocked — so the preview shows
 * an empty frame. A preview only needs the layout, so a fallback font is fine.
 * @param {string} html the rendered email HTML.
 * @returns {string} the HTML with blocking external `@import` rules removed.
 */
function stripBlockingImports(html) {
  return (html || "").replace(/@import\s+url\([^)]*\)\s*;?/gi, "");
}

/**
 * Renders the email a reminder will send, as its recipient will receive it.
 *
 * The markup is dropped into a sandboxed iframe rather than the page: email
 * templates carry their own full-document styling, which would otherwise leak
 * into the admin UI.
 * @param {object} props
 * @param {string} props.templateKey the template to render.
 * @param {number} [props.ruleId] rule whose live rows drive the preview.
 * @param {() => void} [props.onClose] dismisses the preview; the close control
 *   is hidden when the preview is embedded rather than opened on demand.
 * @returns {JSX.Element}
 */
export default function TemplatePreview({ templateKey, ruleId, onClose }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchId = useRef(0);

  useEffect(() => {
    const id = ++fetchId.current;
    setLoading(true);
    setError(null);
    previewTemplate({
      templateKey,
      ...(ruleId ? { reminderRuleId: ruleId } : {}),
    }).then((res) => {
      if (id !== fetchId.current) return;
      setLoading(false);
      if (res.isSuccess) setPreview(res.data);
      else setError(res.message || "Could not render this template");
    });
  }, [templateKey, ruleId]);

  return (
    <div className="rem-preview">
      <div className="rem-preview-head">
        <span className="rem-preview-title">Email preview</span>
        {onClose && (
          <button
            type="button"
            className="rem-icon-btn"
            aria-label="Close preview"
            onClick={onClose}
          >
            ×
          </button>
        )}
      </div>

      {loading && <p className="rem-muted">Rendering…</p>}
      {error && <p className="rem-error">{error}</p>}

      {preview && !loading && (
        <>
          <dl className="rem-preview-meta">
            <dt>Subject</dt>
            <dd>{preview.subject}</dd>
            <dt>To</dt>
            <dd>{preview.recipientName}</dd>
            <dt>Built from</dt>
            <dd>
              {preview.source === "live"
                ? `${preview.rowCount} live row(s)`
                : "sample data — save the rule to preview real rows"}
            </dd>
          </dl>

          {preview.missingVariables.length > 0 && (
            <p className="rem-preview-warn" role="status">
              This template expects {preview.missingVariables.length} value(s)
              the reminder does not supply, so those areas render blank:{" "}
              {preview.missingVariables.slice(0, 8).join(", ")}
              {preview.missingVariables.length > 8 ? "…" : ""}
            </p>
          )}

          <iframe
            className="rem-preview-frame"
            title="Email preview"
            sandbox=""
            srcDoc={stripBlockingImports(preview.html)}
          />
        </>
      )}
    </div>
  );
}
